import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages a sticky add-to-cart bar.
 * The bar appears when the main product form scrolls out of viewport.
 *
 * This component is ISOLATED from the main product form - it does not
 * listen to variantUpdate events or morph its DOM. Instead, it syncs
 * the variant ID directly from the main form's hidden input.
 *
 * @extends Component
 */
class StickyAddToCartBar extends Component {
  #abortController = new AbortController();
  #intersectionObserver = null;
  #mutationObserver = null;
  #mainProductForm = null;
  #mainVariantInput = null;

  connectedCallback() {
    super.connectedCallback();

    this.#findMainProductForm();

    if (this.#mainProductForm) {
      this.#setupIntersectionObserver();
      this.#setupVariantSync();
    }

    // Listen for cart events to show feedback
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  /**
   * Finds the main product form on the page
   */
  #findMainProductForm() {
    const productSection = document.querySelector('.product-information');
    if (!productSection) return;

    // Find the main product form's variant input
    const buyButtonsBlock = productSection.querySelector('.buy-buttons-block');
    const formContainer = buyButtonsBlock || productSection;

    const productFormComponent = formContainer.querySelector('product-form-component');
    if (productFormComponent) {
      this.#mainProductForm = productFormComponent.closest('form') || productFormComponent;
      this.#mainVariantInput = productFormComponent.querySelector('input[name="id"]');
    }
  }

  /**
   * Sets up the IntersectionObserver to monitor the main product form visibility
   */
  #setupIntersectionObserver() {
    if (!this.#mainProductForm || !('IntersectionObserver' in window)) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.#setVisibility(!entry.isIntersecting);
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1],
      }
    );

    this.#intersectionObserver.observe(this.#mainProductForm);
  }

  /**
   * Sets up mutation observer to sync variant ID from main form
   * This is the key isolation mechanism - we watch for changes to the
   * main form's variant input rather than listening to variant events
   */
  #setupVariantSync() {
    if (!this.#mainVariantInput) return;

    // Initial sync
    this.#syncVariantFromMain();

    // Watch for changes to the main form's variant input
    this.#mutationObserver = new MutationObserver(() => {
      this.#syncVariantFromMain();
    });

    this.#mutationObserver.observe(this.#mainVariantInput, {
      attributes: true,
      attributeFilter: ['value'],
    });

    // Also listen for input events in case value is changed programmatically
    this.#mainVariantInput.addEventListener('change', () => this.#syncVariantFromMain(), {
      signal: this.#abortController.signal,
    });

    // Re-check periodically in case the input was replaced (after DOM morph)
    const checkInterval = setInterval(() => {
      if (!document.body.contains(this.#mainVariantInput)) {
        // Main input was removed, find it again
        this.#findMainProductForm();
        if (this.#mainVariantInput) {
          this.#syncVariantFromMain();
          this.#mutationObserver?.disconnect();
          this.#mutationObserver?.observe(this.#mainVariantInput, {
            attributes: true,
            attributeFilter: ['value'],
          });
        }
      }
    }, 500);

    // Clean up interval on disconnect
    this.#abortController.signal.addEventListener('abort', () => {
      clearInterval(checkInterval);
    });
  }

  /**
   * Syncs the variant ID and availability from the main form
   */
  #syncVariantFromMain() {
    // Re-find the input in case it was replaced
    const productSection = document.querySelector('.product-information');
    if (!productSection) return;

    const mainInput = productSection.querySelector('product-form-component input[name="id"]');
    if (!mainInput) return;

    const variantId = mainInput.value;
    const stickyInput = this.querySelector('input[name="id"]');

    if (stickyInput && variantId) {
      stickyInput.value = variantId;
    }

    // Sync button disabled state from main form
    const mainButton = productSection.querySelector('product-form-component [ref="addToCartButton"]');
    const stickyButton = this.querySelector('[ref="addToCartButton"]');

    if (mainButton && stickyButton) {
      const isDisabled = mainButton.disabled || mainButton.getAttribute('disabled') !== null;
      stickyButton.disabled = isDisabled;

      // Sync button text
      const mainButtonText = mainButton.querySelector('.add-to-cart-text__content');
      const stickyButtonText = stickyButton.querySelector('.add-to-cart-text__content');
      if (mainButtonText && stickyButtonText) {
        stickyButtonText.textContent = mainButtonText.textContent;
      }

      // Sync icon visibility (hide cart icon when sold out)
      const mainIcon = mainButton.querySelector('.add-to-cart-icon');
      const stickyIcon = stickyButton.querySelector('.add-to-cart-icon');
      if (stickyIcon) {
        if (mainIcon && !mainIcon.closest('.add-to-cart-text').classList.contains('hidden')) {
          stickyIcon.style.display = '';
        } else if (isDisabled) {
          stickyIcon.style.display = 'none';
        }
      }
    }

    // Sync price from main form
    const mainPrice = productSection.querySelector('product-price [ref="priceContainer"]');
    const stickyPrice = this.querySelector('[ref="priceContainer"]');
    if (mainPrice && stickyPrice && mainPrice.innerHTML !== stickyPrice.innerHTML) {
      stickyPrice.innerHTML = mainPrice.innerHTML;
    }

    // Sync image from main form's current variant
    const mainImage = productSection.querySelector('.product-media-gallery__slide[aria-hidden="false"] img, .product-media img');
    const stickyImage = this.querySelector('[ref="productImage"]');
    if (mainImage && stickyImage) {
      const src = mainImage.src || mainImage.dataset.src;
      if (src && stickyImage.src !== src) {
        // Use smaller size for sticky bar
        const smallSrc = src.replace(/width=\d+/, 'width=100').replace(/&width=\d+/, '&width=100');
        stickyImage.src = smallSrc.includes('width=') ? smallSrc : src + '&width=100';
      }
    }
  }

  /**
   * Sets the visibility of the sticky bar
   * @param {boolean} visible - Whether the bar should be visible
   */
  #setVisibility(visible) {
    const bar = this.querySelector('.sticky-add-to-cart-bar');
    if (!bar) return;

    if (visible) {
      // Sync before showing
      this.#syncVariantFromMain();

      this.removeAttribute('hidden');
      this.setAttribute('data-visible', 'true');
      this.setAttribute('aria-hidden', 'false');
      bar.style.transform = 'translateY(0)';

      // Dispatch event for page spacing
      document.documentElement.setAttribute('data-sticky-bar-visible', 'true');
    } else {
      this.setAttribute('hidden', '');
      this.removeAttribute('data-visible');
      this.setAttribute('aria-hidden', 'true');
      bar.style.transform = 'translateY(100%)';

      document.documentElement.removeAttribute('data-sticky-bar-visible');
    }
  }

  /**
   * Handles the form submission for add to cart
   * @param {Event} event - The submit event
   */
  handleSubmit(event) {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const button = this.querySelector('[ref="addToCartButton"]');
    if (button?.disabled) return;

    // Disable button during submission
    if (button) button.disabled = true;

    const formData = new FormData(form);

    // Get cart items sections for updating
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Error occurred
          this.#showError(response.message);
        } else {
          // Success - show animation
          this.#showSuccess();

          // Dispatch cart update event
          const cartEvent = new CustomEvent(ThemeEvents.cartUpdate, {
            bubbles: true,
            detail: {
              source: 'sticky-add-to-cart',
              sections: response.sections,
            },
          });
          document.dispatchEvent(cartEvent);
        }
      })
      .catch((error) => {
        console.error('Sticky add to cart error:', error);
        this.#showError('An error occurred');
      })
      .finally(() => {
        // Re-enable button
        setTimeout(() => {
          this.#syncVariantFromMain();
        }, 100);
      });
  }

  /**
   * Shows success animation on the button
   */
  #showSuccess() {
    const button = this.querySelector('[ref="addToCartButton"]');
    if (!button) return;

    button.classList.add('atc-added');

    setTimeout(() => {
      button.classList.remove('atc-added');
    }, 2000);
  }

  /**
   * Shows error message
   * @param {string} message - The error message
   */
  #showError(message) {
    const errorElement = this.querySelector('[ref="addToCartTextError"]');
    if (errorElement) {
      errorElement.classList.remove('hidden');
      const textNode = errorElement.querySelector('span:last-child') || errorElement;
      textNode.textContent = message;

      setTimeout(() => {
        errorElement.classList.add('hidden');
      }, 5000);
    }
  }

  /**
   * Handles cart update events
   * @param {CustomEvent} event - The cart update event
   */
  #onCartUpdate = (event) => {
    // If this was from our own submission, already handled
    if (event.detail?.source === 'sticky-add-to-cart') return;

    // Re-sync state after any cart update
    setTimeout(() => {
      this.#syncVariantFromMain();
    }, 100);
  };
}

if (!customElements.get('sticky-add-to-cart-bar')) {
  customElements.define('sticky-add-to-cart-bar', StickyAddToCartBar);
}
